import _ from 'lodash';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserContext } from '../../context/UserContext';
import { renderSpinner } from '../../utils/Utility';
import I3Modal from '../../components/Layout/I3Modal';
import I3ModalConfirmation from '../../components/Layout/I3ModalConfirmation';
import { PERMISSIONS, ROUTES } from '../../constants';
import { Unauthorized } from '../../components/Unauthorized';
import { RetrospectiveDataGrid } from './RetrospectiveDataGrid';
import {
  deleteRetrospective,
  getRetrospectiveByYearCompanyAndUsernames,
  notifyRetrospective,
} from '../../utils/API/retrospective';
import { RetrospectiveForm } from './RetrospectiveForm';
import { MultiSelectUser } from '../../components/Form/MultiSelectUser';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import { PlusCircle } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import MailForm from '../../components/MailForm';
import { getCompanies } from '../../utils/API/companies';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { RetrospectiveReports } from './RetrospectiveReports';
import {
  getRetrospectiveGoalsReportByYearCompanyAndUsernames,
  getRetrospectiveSingleGoalsByYearCompanyAndUsernames,
} from '../../utils/API/retrospectiveGoals';

export const RetrospectivePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { employeesQueryA: employees } = useContext(EmployeeListContext);
  const { hasClaim } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [retrospectives, setRetrospectives] = useState([]);
  const [retrospectiveGoalsReport, setRetrospectiveGoalsReport] = useState();
  const [retrospectiveSingleGoalsReport, setRetrospectiveSingleGoalsReport] =
    useState();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedRetrospective, setSelectedRetrospective] = useState(undefined);
  const availableYears = _.range(2023, moment().year() + 1);
  const [selectedYear, setSelectedYear] = useState();
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState();
  const [selectedTabKey, setSelectedTabKey] = useState('retrospectives');
  const [isLoadingFormUsers, setIsLoadingFormUsers] = useState(false);
  const [formAvailableUsers, setFormAvailableUsers] = useState([]);
  const [isDeletingRetrospective, setIsDeletingRetrospective] = useState(false);
  const lastFetchParams = useRef();

  if (!hasClaim(PERMISSIONS.RETROSPECTIVES_VIEW)) {
    return <Unauthorized />;
  }

  const filtersInitialized = useMemo(
    () => selectedYear && selectedCompanyId && selectedUserIds,
    [selectedYear, selectedCompanyId, selectedUserIds]
  );

  useEffect(() => {
    if (state?.filters) {
      // restore previous filters
      setSelectedYear(state.filters.year);
      setSelectedCompanyId(state.filters.company);
      setSelectedUserIds(state.filters.users);
    } else {
      // initialize filters
      setSelectedYear(moment().year());
      setSelectedCompanyId(1);
      setSelectedUserIds([]);
    }
  }, [state]);

  useEffect(() => {
    if (!filtersInitialized) {
      return;
    }

    const usernames =
      employees
        .filter((u) => selectedUserIds?.includes(u.id))
        .map((u) => u.username)
        .sort() ?? [];

    const nextParams = {
      year: selectedYear,
      company: selectedCompanyId,
      usernames,
    };

    if (_.isEqual(lastFetchParams.current, nextParams)) {
      return;
    }

    lastFetchParams.current = nextParams;
    setIsLoading(true);
    const getData = async () => {
      setRetrospectives(
        await getRetrospectiveByYearCompanyAndUsernames(
          selectedYear,
          selectedCompanyId,
          usernames
        )
      );
      setRetrospectiveGoalsReport(
        await getRetrospectiveGoalsReportByYearCompanyAndUsernames(
          selectedYear,
          selectedCompanyId,
          usernames
        )
      );
      setRetrospectiveSingleGoalsReport(
        await getRetrospectiveSingleGoalsByYearCompanyAndUsernames(
          selectedYear,
          selectedCompanyId,
          usernames
        )
      );
    };
    getData().finally(() => setIsLoading(false));
  }, [
    employees,
    selectedUserIds,
    selectedYear,
    selectedCompanyId,
    filtersInitialized,
  ]);

  useEffect(() => {
    if (!filtersInitialized) {
      return;
    }

    const filteredUsers = employees.filter(
      (u) => u.company === selectedCompanyId
    );
    const filteredIds = filteredUsers.map((u) => u.id);
    const selectedIds = (selectedUserIds ?? []).filter((id) =>
      filteredIds.includes(id)
    ); // keep selected only users that belong to the given company

    if (!_.isEqual(selectedIds, selectedUserIds)) {
      setSelectedUserIds(selectedIds);
    }

    setAvailableUsers(filteredUsers);
  }, [employees, selectedCompanyId, selectedUserIds, filtersInitialized]);

  useEffect(() => {
    // fetch companies once
    const loadCompanies = async () => {
      setAvailableCompanies(await getCompanies());
    };
    loadCompanies();
  }, []);

  const navigateToEditPage = (id) => {
    navigate(`${ROUTES.RETROSPECTIVE}/${id}`, {
      state: {
        filters: {
          year: selectedYear,
          company: selectedCompanyId,
          users: selectedUserIds,
        },
      },
    });
  };

  const handleAddClick = async () => {
    setIsLoadingFormUsers(true);
    try {
      const year = moment().year();
      // Carica le retrospettive solo per l'anno corrente
      const retrospectivesThisYear =
        await getRetrospectiveByYearCompanyAndUsernames(
          year,
          selectedCompanyId,
          [] // no filter by usernames - get all for the company
        );

      const retrospectiveUsernames = new Set(
        retrospectivesThisYear.map((r) => r.user.username)
      );

      // Filtra gli employees che non hanno retrospettiva quest'anno e sono ancora attivi
      const availableFormUsers = employees.filter(
        (emp) => !retrospectiveUsernames.has(emp.username) && !emp.outgoing_date
      );

      setFormAvailableUsers(availableFormUsers);
      setShowUpsertModal(true);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoadingFormUsers(false);
    }
  };

  const handleEditClick = navigateToEditPage;

  const handleDeleteClick = (retrospective) => {
    setSelectedRetrospective(retrospective);
    setShowDeleteConfirmationModal(true);
  };

  const handleConfirmDeleteClick = async () => {
    setIsDeletingRetrospective(true);
    try {
      await deleteRetrospective(selectedRetrospective.id);
      setRetrospectives(
        retrospectives.filter((i) => i.id !== selectedRetrospective.id)
      );
    } finally {
      setShowDeleteConfirmationModal(false);
      setIsDeletingRetrospective(false);
    }
  };

  const handleNotifyClick = (retrospective) => {
    setSelectedRetrospective(retrospective);
    setShowNotificationModal(true);
  };

  const sendNotification = async (data) => {
    const notifiedRetrospective = await notifyRetrospective(
      selectedRetrospective.id,
      data
    );
    setRetrospectives(
      retrospectives.map((retrospective) =>
        retrospective.id === notifiedRetrospective.id
          ? notifiedRetrospective
          : retrospective
      )
    );
  };

  const getMailSubject = useCallback(() => {
    if (!selectedRetrospective) {
      return '';
    }
    return `Retrospettiva ${selectedRetrospective.year}`;
  }, [selectedRetrospective]);

  const getMailBody = useCallback(() => {
    if (!selectedRetrospective || !selectedRetrospective.currentGoals?.length) {
      return '';
    }
    const text = [
      `Ciao ${selectedRetrospective.user.first_name},<br/>`,
      'ecco un recap degli obiettivi che abbiamo concordato durante la tua retrospettiva annuale.<br/>',
      'In caso ci fosse qualcosa che non ti torna, non esitare a dirmelo.<br/>',
      '<br/>',
    ];
    if (selectedRetrospective.feedbackForm) {
      text.push(
        `Inoltre, ti chiederemmo anche un feedback (anonimo) per sapere com'è andata la tua retrospettiva e per aiutarci a organizzare al meglio le prossime: <a href="${selectedRetrospective.feedbackForm}" target="_blank">${selectedRetrospective.feedbackForm}</a><br/>`,
        '<br/>'
      );
    }
    text.push(
      'Obiettivi:',
      '<ul>',
      ...selectedRetrospective.currentGoals.map(
        (goal) => `<li>${goal.description}</li>`
      ),
      '</ul>',
      '<br/>'
    );
    text.push('Grazie!');
    return text.join('');
  }, [selectedRetrospective]);

  return filtersInitialized ? (
    <>
      {isLoadingFormUsers && renderSpinner(true)}
      {isDeletingRetrospective && renderSpinner(true)}
      <div className="content__wrapper">
        <div className="headerPage content">
          <div className="title">Retrospettiva</div>
          <div className="spacer" />
          <div className="d-flex">
            <div className="form-group">
              <select
                className="yearContainer"
                defaultValue={selectedYear}
                onChange={({ target: { value } }) => setSelectedYear(+value)}
              >
                {availableYears.map((n) => (
                  <option value={+n} key={+n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="buttonGroup">
              {availableCompanies.length > 0 && (
                <TextField
                  sx={{ width: 200 }}
                  select
                  fullWidth
                  size="small"
                  label="Azienda"
                  value={selectedCompanyId}
                  onChange={({ target: { value } }) =>
                    setSelectedCompanyId(value)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                >
                  {availableCompanies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <MultiSelectUser
                users={availableUsers}
                selected={selectedUserIds}
                setSelected={setSelectedUserIds}
              />
              <Button
                variant="primary"
                onClick={() => handleAddClick()}
                disabled={isLoadingFormUsers}
              >
                <PlusCircle />
                Inserisci
              </Button>
            </div>
          </div>
        </div>
        <div className="content__overflow">
          <div className="tabsContainer" style={{ flex: '1 1 0px' }}>
            {isLoading ? (
              renderSpinner(true)
            ) : (
              <Tabs
                className="mb-3 tabs-chart d-flex justify-content-start"
                activeKey={selectedTabKey}
                onSelect={(k) => setSelectedTabKey(k)}
                mountOnEnter
              >
                <Tab
                  eventKey="retrospectives"
                  title={<span className="py-2">Retro</span>}
                >
                  <RetrospectiveDataGrid
                    data={retrospectives}
                    year={selectedYear}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                    onNotifyClick={handleNotifyClick}
                  />
                </Tab>
                <Tab
                  eventKey="reports"
                  title={<span className="py-2">Report</span>}
                >
                  {!_.isNil(retrospectiveGoalsReport) ? (
                    <RetrospectiveReports
                      data={retrospectiveGoalsReport}
                      retroSingleGoals={retrospectiveSingleGoalsReport}
                    />
                  ) : (
                    <div>Report non disponibile</div>
                  )}
                </Tab>
              </Tabs>
            )}
          </div>
        </div>
      </div>
      <I3Modal
        show={showUpsertModal}
        onHide={() => setShowUpsertModal(false)}
        title="Aggiungi retrospettiva"
      >
        <RetrospectiveForm
          availableUsers={formAvailableUsers}
          onFetchUsers={async (date) => {
            setIsLoadingFormUsers(true);
            try {
              const year = date.getFullYear();
              // Carica le retrospettive per l'anno selezionato
              const retrospectivesForYear =
                await getRetrospectiveByYearCompanyAndUsernames(
                  year,
                  selectedCompanyId,
                  []
                );

              const retrospectiveUsernames = new Set(
                retrospectivesForYear.map((r) => r.user.username)
              );

              // Filtra gli employees che non hanno retrospettiva per quell'anno e sono ancora attivi
              const availableFormUsers = employees.filter(
                (emp) =>
                  !retrospectiveUsernames.has(emp.username) &&
                  !emp.outgoing_date
              );

              setFormAvailableUsers(availableFormUsers);
            } catch (error) {
              console.error('Error fetching data:', error);
            } finally {
              setIsLoadingFormUsers(false);
            }
          }}
          onClose={() => setShowUpsertModal(false)}
          onSubmitSuccess={(data) => navigateToEditPage(data.id)}
        />
      </I3Modal>
      <I3ModalConfirmation
        show={showDeleteConfirmationModal}
        onHide={() =>
          !isDeletingRetrospective && setShowDeleteConfirmationModal(false)
        }
        title="Cancella retrospettiva"
        questionTitle="Vuoi cancellare questa retrospettiva?"
        onConfirm={handleConfirmDeleteClick}
        confirmDisabled={isDeletingRetrospective}
      />
      <I3Modal
        show={showNotificationModal}
        onHide={() => setShowNotificationModal(false)}
        title="Invia obiettivi"
      >
        <MailForm
          htmlEditor={true}
          subject={getMailSubject()}
          body={getMailBody()}
          users={[selectedRetrospective?.user]}
          onSend={sendNotification}
          onClose={() => setShowNotificationModal(false)}
        />
      </I3Modal>
    </>
  ) : (
    <></>
  );
};
